import { motion } from "framer-motion";
import { Clock10Icon, DollarSignIcon, TrendingUpIcon } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/context/i18n";
import { formatPrice } from "@/utils/monetary-formatter";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { RankingCard } from "./components/RankingCard";
import { SalesAreaChart } from "./components/SalesAreaChart";
import { useDashboard } from "./dashboard.hooks";
import { TDashboardPeriod, TDashboardProductType } from "./dashboard.types";

export function DashboardPage() {
  const { t } = useI18n();
  const {
    productType,
    setProductType,
    period,
    setPeriod,
    overviewData,
    isLoading,
    productsRankingData,
    plansRankingData,
    chartData,
    isLoadingChart,
    selectedYear,
    setSelectedYear,
    availableTypes,
    isLoadingProductTypes,
  } = useDashboard();

  // Formata taxa de conversão (dividindo por 100 para obter porcentagem)
  const formatConversionRate = (value: number) => {
    return `${(value / 100).toFixed(2).replace(".", ",")}%`;
  };

  // Gera o título do card de vendas baseado no período
  const getSalesTitle = () => {
    if (period === "total") {
      return "Vendas Totais";
    }
    return `Vendas nos últimos ${period} dias`;
  };

  // Cards dinâmicos baseados nos dados da API
  const stats = React.useMemo(() => {
    if (!overviewData) {
      return [
        {
          title: getSalesTitle(),
          value: "-",
          icon: <div className="flex items-center justify-center rounded-full bg-chart-2/10 p-2">
            <Clock10Icon className="h-4 w-4 text-chart-2" />
          </div>,
        },
        {
          title: "Ticket Médio",
          value: "-",
          icon: <div className="flex items-center justify-center rounded-full bg-yellow-500/20 p-2">
            <DollarSignIcon className="h-4 w-4 text-yellow-500" />
          </div>,
        },
        {
          title: "Taxa de Conversão",
          value: "-",
          icon: <div className="flex items-center justify-center rounded-full bg-green-500/20 p-2">
            <TrendingUpIcon className="h-4 w-4 text-green-500" />
          </div>,
        },
        {
          title: "Receita",
          value: "-",
          icon: <div className="flex items-center justify-center rounded-full bg-green-500/20 p-2">
            <DollarSignIcon className="h-4 w-4 text-green-500" />
          </div>,
        },
      ];
    }

    return [
      {
        title: getSalesTitle(),
        value: formatPrice(overviewData.monthly_sales),
        icon: <div className="flex items-center justify-center rounded-full bg-chart-2/10 p-2">
          <Clock10Icon className="h-4 w-4 text-chart-2" />
        </div>,
      },
      {
        title: "Ticket Médio",
        value: formatPrice(overviewData.average_ticket),
        icon: <div className="flex items-center justify-center rounded-full bg-yellow-500/20 p-2">
          <DollarSignIcon className="h-4 w-4 text-yellow-500" />
        </div>,
      },
      {
        title: "Taxa de Conversão",
        value: formatConversionRate(overviewData.conversion_rate),
        icon: <div className="flex items-center justify-center rounded-full bg-green-500/20 p-2">
          <TrendingUpIcon className="h-4 w-4 text-green-500" />
        </div>,
      },
      {
        title: "Receita",
        value: formatPrice(overviewData.revenue),
        icon: <div className="flex items-center justify-center rounded-full bg-green-500/20 p-2">
          <DollarSignIcon className="h-4 w-4 text-green-500" />
        </div>,
      },
    ];
  }, [overviewData, productType, period]);

  return (
    <div className="w-full h-full flex flex-col space-y-3 sm:space-y-4 lg:space-y-5">
      <div className="flex flex-col flex-shrink-0">
        <h1 className="text-3xl font-bold tracking-tight">
          {t("dashboard.title")}
        </h1>
      </div>

      {(isLoadingProductTypes || availableTypes.length > 0) && (
        <div className="w-full overflow-x-auto flex-shrink-0">
          <DashboardTabs
            activeTab={productType}
            onTabChange={setProductType}
            availableTypes={availableTypes}
            isLoading={isLoadingProductTypes}
            translate={t}
          />
        </div>
      )}

      <div className="flex flex-col gap-2 flex-shrink-0">
        <div className="w-full overflow-x-auto">
          <PeriodFilters
            activePeriod={period}
            onPeriodChange={setPeriod}
          />
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 flex-shrink-0">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="w-full h-full"
          >
            <Card className="w-full h-full flex flex-col">
              <CardHeader className="flex flex-row items-center justify-between pb-2 flex-shrink-0">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                {stat.icon}
              </CardHeader>
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-2xl font-bold w-full text-center">
                  {isLoading ? "Carregando..." : stat.value}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-4 flex-1 min-h-0">
        {/* Card de Ranking */}
        <div className="lg:col-span-1 order-2 lg:order-1 w-full h-full min-h-0">
          <RankingCard
            productsRankingData={productsRankingData}
            plansRankingData={plansRankingData}
            isLoading={isLoading}
            isPlansView={productType === "all"}
          />
        </div>

        {/* Gráfico de Vendas */}
        <div className="lg:col-span-3 order-1 lg:order-2 w-full h-full min-h-0">
          <SalesAreaChart
            title="Vendas"
            productType={productType}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            chartData={chartData}
            isLoading={isLoadingChart}
          />
        </div>
      </div>
    </div>
  );
}
interface DashboardTabsProps {
  activeTab: TDashboardProductType;
  onTabChange: (tab: TDashboardProductType) => void;
  availableTypes: string[];
  isLoading?: boolean;
  translate: (key: string) => string;
}

function productTypeLabel(type: string, translate: (key: string) => string): string {
  const key = `product.type.${type}`;
  const label = translate(key);
  return label !== key ? label : type;
}

export function DashboardTabs({
  activeTab,
  onTabChange,
  availableTypes,
  isLoading,
  translate,
}: DashboardTabsProps) {
  const tabs = React.useMemo(() => {
    const items: { id: TDashboardProductType; label: string }[] = [
      { id: "all", label: translate("dashboard.tabs.all") },
    ];
    for (const type of availableTypes) {
      items.push({ id: type, label: productTypeLabel(type, translate) });
    }
    return items;
  }, [availableTypes, translate]);

  if (isLoading) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        {translate("generic.loading")}
      </p>
    );
  }

  return (
    <div className="relative size-full">
      <div className="box-border content-stretch flex flex-row gap-[33px] items-center justify-start p-0 relative size-full">
        <div className="relative shrink-0">
          <div className="box-border content-stretch flex flex-row items-center justify-start p-0 relative">
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                label={tab.label}
                isActive={activeTab === tab.id}
                onClick={() => onTabChange(tab.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface TabButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ label, isActive, onClick }: TabButtonProps) {
  return (
    <div className="h-14 relative shrink-0">
      <div
        className={`absolute inset-0 pointer-events-none ${
          isActive ? "border-[#ffffff] border-[0px_0px_3px] border-solid" : ""
        }`}
      />
      <div className="flex flex-row items-center justify-center relative size-full">
        <div className="box-border content-stretch flex flex-row h-14 items-center justify-center px-[30px] py-[15px] relative">
          <Button variant={isActive ? "default" : "outline"} onClick={onClick}>
            <p className="block leading-[24.5667px] whitespace-pre">{label}</p>
          </Button>
        </div>
      </div>
    </div>
  );
}

interface PeriodFiltersProps {
  activePeriod: TDashboardPeriod;
  onPeriodChange: (period: TDashboardPeriod) => void;
}

export function PeriodFilters({
  activePeriod,
  onPeriodChange,
}: PeriodFiltersProps) {
  const [daysInput, setDaysInput] = React.useState<string>(
    activePeriod === "total" ? "30" : String(activePeriod)
  );

  React.useEffect(() => {
    if (activePeriod !== "total") {
      setDaysInput(String(activePeriod));
    }
  }, [activePeriod]);

  const handleDaysChange = (value: string) => {
    setDaysInput(value);
  };

  const handleApplyClick = () => {
    const days = parseInt(daysInput, 10);
    if (!isNaN(days) && days > 0) {
      onPeriodChange(days);
    } else {
      setDaysInput("30");
      onPeriodChange(30);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleApplyClick();
    }
  };

  const handleTotalClick = () => {
    onPeriodChange("total");
    setDaysInput("30");
  };

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold">Período</h2>
      <p className="text-sm text-muted-foreground">
        Selecione "Total" para ver todos os dados ou informe um período específico em dias
      </p>
      <div className="relative size-full">
        <div className="box-border content-stretch flex flex-row gap-4 items-center justify-start p-0 relative size-full">
          <div className="relative shrink-0">
            <div className="box-border content-stretch flex flex-row items-center justify-start p-0 relative">
              <PeriodButton
                label="Total"
                isActive={activePeriod === "total"}
                onClick={handleTotalClick}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium whitespace-nowrap">
              Últimos
            </label>
            <Input
              type="number"
              min="1"
              value={daysInput}
              onChange={(e) => handleDaysChange(e.target.value)}
              onKeyDown={handleInputKeyDown}
              className="w-20"
            />
            <label className="text-sm font-medium whitespace-nowrap">
              dias
            </label>
            <Button onClick={handleApplyClick} variant="outline" size="sm">
              Aplicar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PeriodButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function PeriodButton({ label, isActive, onClick }: PeriodButtonProps) {
  return (
    <div className="h-14 relative shrink-0">
      <div
        className={`absolute inset-0 pointer-events-none ${
          isActive ? "border-[#ffffff] border-[0px_0px_3px] border-solid" : ""
        }`}
      />
      <div className="flex flex-row items-center justify-center relative size-full">
        <div className="box-border content-stretch flex flex-row h-14 items-center justify-center px-[30px] py-[15px] relative">
          <Button variant={isActive ? "default" : "outline"} onClick={onClick}>
            <p className="block leading-[24.5667px] whitespace-pre">{label}</p>
          </Button>
        </div>
      </div>
    </div>
  );
}
